import React, { useEffect, useRef, useState } from "react";
import { FaVideo, FaStar, FaCalendarAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import Navbar from "../component/common/Navbar";
import HeroSection from "../component/core/Homecomponent/HeroSection";
import ServiceCard from "../component/core/Homecomponent/ServiceCard";
import TestimonialCard from "../component/core/Homecomponent/TestimonialCard";
import TempleCard from "../component/core/Homecomponent/TempleCard";
import SectionWrapper from "../component/core/Homecomponent/SectionWrapper";
import SectionHeader from "../component/core/Homecomponent/SectionHeader";
import usePageTitle from "../hooks/usePageTitle";
import Footer from "../component/common/Footer";
import { useLanguage } from "../i18n/LanguageContext";

const Homepage = () => {
  usePageTitle('One App for all your Devotional Needs');
  const { t } = useLanguage();
  
  // Animation state and refs
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});
  
  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all sections
    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Add ref to section
  const addRef = (id) => (ref) => {
    if (ref) {
      sectionRefs.current[id] = ref;
      ref.id = id;
    }
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-amber-50">
      <Navbar/>

      {/* Hero Section */}
      <HeroSection
        title={t('experienceDivineDarshan')}
        subtitle={t('connectWithSacredTemples')}
        secondaryButtonText={t('watchLiveDarshan')}
        secondaryButtonLink="/live"
        backgroundImage="https://images.unsplash.com/photo-1582510003544-4d00b7f74220?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
      />

      {/* About Us Section */}
      <SectionWrapper className="py-16 bg-white">
        <div 
          ref={addRef('about')}
          className={`max-w-4xl mx-auto text-center transition-all duration-1000 ${
            isVisible.about 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <SectionHeader 
            title={t('bridgingTraditionWithTechnology')}
            subtitle={t('traditionTechnologySubtitle')}
          />
          <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-3 rounded-full font-semibold hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl animate-pulse hover:animate-none">
            {t('learnMore')}
          </button>
        </div>
      </SectionWrapper>

      {/* Services Section */}
      <SectionWrapper className="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
        <div 
          ref={addRef('services-header')}
          className={`transition-all duration-1000 delay-200 ${
            isVisible['services-header'] 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
        <SectionHeader title={t('divineServicesAtYourFingertips')} />
        </div>
        
        <div 
          ref={addRef('services')}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto transition-all duration-1000 delay-300 ${
            isVisible.services 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="transform hover:scale-105 transition-all duration-300 hover:rotate-1">
          <ServiceCard
              icon={<FaVideo className="text-white text-2xl animate-bounce" />}
            title={t('liveDarshanTitle')}
            description={t('liveDarshanDescription')}
            buttonText={t('joinNow')}
            buttonLink="/live"
          />
          </div>
          
          <div className="transform hover:scale-105 transition-all duration-300 hover:-rotate-1">
          <ServiceCard
              icon={<FaStar className="text-white text-2xl animate-pulse" />}
            title={t('astrologyConsultationTitle')}
            description={t('astrologyConsultationDescription')}
            buttonText={t('consultNow')}
            buttonLink="/astrology"
          />
          </div>
          
          <div className="transform hover:scale-105 transition-all duration-300 hover:rotate-1">
          <ServiceCard
              icon={<FaCalendarAlt className="text-white text-2xl animate-spin" style={{animationDuration: '3s'}} />}
            title={t('dailyPanchangTitle')}
            description={t('dailyPanchangDescription')}
            buttonText={t('viewPanchang')}
            buttonLink="/panchang"
          />
          </div>
        </div>
      </SectionWrapper>


      {/* Testimonials */}
      <SectionWrapper className="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
        <div 
          ref={addRef('testimonials-header')}
          className={`transition-all duration-1000 delay-200 ${
            isVisible['testimonials-header'] 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
        <SectionHeader title={t('whatDevoteesSay')} />
        </div>
        
        {/* Trusted by Pandits line */}
        <div 
          ref={addRef('trusted-pandits')}
          className={`text-center mb-8 transition-all duration-1000 delay-100 ${
            isVisible['trusted-pandits'] 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-gray-600 text-sm italic">
            {t('trustedByPandits')}
          </p>
        </div>
        
        <div 
          ref={addRef('testimonials')}
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto transition-all duration-1000 delay-300 ${
            isVisible.testimonials 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="transform hover:scale-105 transition-all duration-500 hover:shadow-2xl">
          <TestimonialCard
            name="Aviraj"
            review={t('avirajReview')}
            avatarInitial="A"
          />
          </div>
          
          <div className="transform hover:scale-105 transition-all duration-500 hover:shadow-2xl">
          <TestimonialCard
            name="Akash"
            review={t('akashReview')}
            avatarInitial="A"
          />
          </div>
        </div>
      </SectionWrapper>

      {/* Featured Temples */}
      <SectionWrapper className="py-16 bg-white">
        <div 
          ref={addRef('temples-header')}
          className={`transition-all duration-1000 delay-200 ${
            isVisible['temples-header'] 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <SectionHeader title={t('featuredTemples')} />
        </div>
        
        <div 
          ref={addRef('temples')}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto transition-all duration-1000 delay-300 ${
            isVisible.temples 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="transform hover:scale-110 transition-all duration-500 hover:rotate-2 hover:shadow-2xl">
            <TempleCard
              name={t('goldenTemple')}
              location={t('amritsarPunjab')}
              image="https://plus.unsplash.com/premium_photo-1697730331435-92e07494db43?q=80&w=1325&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            />
          </div>
          
          <div className="transform hover:scale-110 transition-all duration-500 hover:-rotate-2 hover:shadow-2xl">
            <TempleCard
              name={t('kashiVishwanath')}
              location={t('varanasiUp')}
              image="https://plus.unsplash.com/premium_photo-1661963629241-52c812d5c7f8?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            />
          </div>
          
          <div className="transform hover:scale-110 transition-all duration-500 hover:rotate-2 hover:shadow-2xl">
            <TempleCard
              name={t('tirupatiBalaji')}
              location={t('tirupatiAp')}
              image="https://images.unsplash.com/photo-1741003412854-bd4b264c4af3?q=80&w=1632&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            />
          </div>
          
          <div className="transform hover:scale-110 transition-all duration-500 hover:-rotate-2 hover:shadow-2xl">
            <TempleCard
              name={t('meenakshiTemple')}
              location={t('maduraiTn')}
              image="https://plus.unsplash.com/premium_photo-1697729444936-8c6a6f643312?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            />
          </div>
        </div>
        
        <div 
          ref={addRef('temples-cta')}
          className={`text-center mt-8 transition-all duration-1000 delay-500 ${
            isVisible['temples-cta'] 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <Link to="/temples" className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-3 rounded-full font-semibold hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl inline-block animate-pulse hover:animate-none">
            {t('viewAllTemples')}
          </Link>
        </div>
      </SectionWrapper>

      {/* CTA Banner */}
      <section 
        ref={addRef('cta')}
        className={`py-16 bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-1000 ${
          isVisible.cta 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 animate-pulse">{t('beginSpiritualJourney')}</h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            {t('joinMillionsDevotees')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-110 shadow-xl border-2 border-white hover:shadow-2xl animate-bounce hover:animate-none">
              {t('downloadApp')}
            </button>
            <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-110 shadow-xl border-2 border-white hover:shadow-2xl animate-bounce hover:animate-none" style={{animationDelay: '0.5s'}}>
              {t('exploreServices')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
        <Footer/>
    </div>
  );
};

export default Homepage;
